import { MAX_CASE_FILE_BYTES, MAX_CASE_FILES } from '@/lib/api-types'

/**
 * Client-side rules for file uploads — enforced before anything leaves the
 * browser. The API enforces the same limits (one extension allowlist; a 4 MB
 * cap for PO documents, sized to the Apex REST base64 ceiling; a 10 MB cap
 * for case files, which upload directly from the API); this exists so people
 * get instant, specific feedback instead of a failed upload.
 */

/** One PO document per request — the contract takes exactly one file part. */
export const MAX_PO_FILE_BYTES = 4 * 1024 * 1024 // 4 MB

/** Mirror of the API's ALLOWED_EXTENSIONS (lib/salesforce/files.ts). */
export const ALLOWED_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'txt',
  'log',
  'csv',
  'json',
  'xml',
  'zip',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'eml',
  'msg',
] as const

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

export interface FileValidationError {
  name: string
  reason: string
}

/** Shared allowlist check — null when the extension is acceptable. */
function extensionError(name: string): FileValidationError | null {
  const ext = fileExtension(name)
  if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
    return {
      name,
      reason: ext ? `.${ext} files aren't supported` : 'files without an extension aren’t supported',
    }
  }
  return null
}

/** Null when the file is acceptable as the PO document; the rejection reason otherwise. */
export function validatePoFile(file: File): FileValidationError | null {
  const badExtension = extensionError(file.name)
  if (badExtension) return badExtension
  if (file.size > MAX_PO_FILE_BYTES) {
    return { name: file.name, reason: 'over the 4 MB limit' }
  }
  return null
}

/** Null when the file is acceptable as a request attachment; the rejection reason otherwise. */
export function validateCaseFile(file: File): FileValidationError | null {
  const badExtension = extensionError(file.name)
  if (badExtension) return badExtension
  if (file.size > MAX_CASE_FILE_BYTES) {
    return { name: file.name, reason: 'over the 10 MB limit' }
  }
  return null
}

export interface CaseFilesSelection {
  /** Files that passed — safe to add to the pending set, in input order. */
  accepted: File[]
  /** One entry per rejected file, with the specific reason. */
  rejected: FileValidationError[]
}

/**
 * Validates a batch of request files as the user picks them: per-file rules
 * via validateCaseFile, duplicate names rejected (case-insensitive, including
 * against `existingNames` already in the selection), and the file cap counted
 * across existing + newly accepted.
 */
export function validateCaseFiles(
  candidates: File[],
  existingNames: string[] = [],
): CaseFilesSelection {
  const accepted: File[] = []
  const rejected: FileValidationError[] = []
  const seen = new Set(existingNames.map((name) => name.toLowerCase()))
  for (const file of candidates) {
    const error = validateCaseFile(file)
    if (error) {
      rejected.push(error)
      continue
    }
    if (seen.has(file.name.toLowerCase())) {
      rejected.push({ name: file.name, reason: 'already added' })
      continue
    }
    if (existingNames.length + accepted.length >= MAX_CASE_FILES) {
      rejected.push({ name: file.name, reason: `more than ${MAX_CASE_FILES} files per request` })
      continue
    }
    seen.add(file.name.toLowerCase())
    accepted.push(file)
  }
  return { accepted, rejected }
}
